import Icon from '@ailibs/feather-react-ts';
import { Box, Flex, Image, ImageProps, Input, Text, useToast } from '@chakra-ui/react';
import { useRef } from 'react';
import AvatarDefault from 'src/assets/images/avata-default.png';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';

type ProfileImageProps = {
  imageProps?: Omit<ImageProps, 'src'>;
  src?: string;
  changeImageText?: string;
  onImageChange?: (image: File) => void;
  isLoading?: boolean;
};

export const ProfileImage = (props: ProfileImageProps) => {
  const { t } = useTranslation();

  const {
    src = AvatarDefault,
    imageProps,
    changeImageText = t('components.profile_image.profile_image_change_text'),
    onImageChange,
    isLoading,
  } = props;
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  return (
    <Flex position='relative' role='group'>
      <Image width='150px' height='150px' objectFit='cover' borderRadius='46px' src={src || AvatarDefault} {...imageProps} />

      <Box
        visibility='hidden'
        opacity={0}
        transition='0.2s'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        w='150px'
        h='150px'
        borderRadius='46px'
        position='absolute'
        left={0}
        top={0}
        css={{
          '&::before': {
            content: '""',
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: '0',
            backgroundColor: 'var(--qgx-theme-colors-white)',
            opacity: '0.6',
            borderRadius: '46px',
          },

          '> svg': {
            color: 'var(--qgx-theme-colors-brand-500)',
            zIndex: 999,
          },
        }}
        _groupHover={{
          visibility: 'visible',
          opacity: 1,
        }}
      >
        <Icon name='camera' />
        <Text
          color='brand.500'
          fontSize='14px'
          fontWeight='500'
          textDecor='underline'
          cursor='pointer'
          zIndex={999}
          onClick={() => uploadInputRef?.current?.click()}
        >
          {changeImageText}
        </Text>
      </Box>

      <Input
        ref={uploadInputRef}
        type='file'
        accept='image/*'
        display='none'
        onChange={event => {
          if (!event.target.files) return;

          const image = event.target.files[0];
          const isImageValid = image.type.includes('image/');

          if (!isImageValid) {
            toast({
              title: t('components.profile_image.profile_image_invalid_file_title'),
              description: t('components.profile_image.profile_image_invalid_file_description'),
            });
            return;
          }

          onImageChange?.(image);

          if (uploadInputRef?.current !== null) {
            uploadInputRef.current.value = '';
          }
        }}
      />

      <CenteredSpinner isLoading={isLoading} />
    </Flex>
  );
};
